// src/hr/portal/ReportsPage.js -- HR reports (spec §16.3 S6). Six reports, each viewable here as a table
// (GET /admin/reports/:name, JSON) and downloadable as CSV: the "Download CSV" link points at the same
// report with format=csv, so the browser downloads it with the session cookie like any link. The
// server checks the inputs and its messages show inline ('Pick a range of 400 days or less').
// These reports are HR-only, so they show leave types (Who's Out never does).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FiAlertTriangle, FiBarChart2, FiCalendar, FiDownload, FiList, FiPlay, FiRefreshCw, FiUsers
} from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Field, Skeleton } from '../kit';
import { firstOfMonth, lastOfMonth, monthOf } from '../timeoff/dates';
import { fullName } from './portalUtils';
import { FormError } from './FormError';

const MAX_ROWS_ON_SCREEN = 200;

const REPORTS = [
  { name: 'balances', title: 'Balances by person', text: 'Allowance, carried over, used, scheduled, pending and available for a benefit year.', icon: FiUsers, inputs: 'year' },
  { name: 'usage', title: 'Usage by month', text: 'Days taken per type, month by month, for a date range.', icon: FiBarChart2, inputs: 'range' },
  { name: 'year-end', title: 'Carryover & payouts', text: 'What each year-end close-out carried over and paid out.', icon: FiRefreshCw, inputs: 'year' },
  { name: 'whos-out', title: "Who's out", text: 'Everyone out on each workday of a range, with the type.', icon: FiCalendar, inputs: 'range' },
  { name: 'overdue', title: 'Overdue & expiring', text: 'Late policy acknowledgments and reminders coming due.', icon: FiAlertTriangle, inputs: null },
  { name: 'ledger', title: 'Balance ledger', text: 'Every balance change — the full record, kept for years.', icon: FiList, inputs: 'ledger' }
];

// The inputs a report starts with: the current benefit year; this month for who's out; last year's
// close-out; every year of the ledger.
function defaultsFor(report, yearRange, today) {
  const year = yearRange && yearRange.year ? yearRange.year : Number(String(today).slice(0, 4));
  switch (report.name) {
    case 'balances': return { year: String(year) };
    case 'year-end': return { year: String(year - 1) };
    case 'usage': return { from: (yearRange && yearRange.start) || `${year}-01-01`, to: (yearRange && yearRange.end) || `${year}-12-31` };
    case 'whos-out': return { from: firstOfMonth(monthOf(today)), to: lastOfMonth(monthOf(today)) };
    case 'ledger': return { year: '', employeeId: '' };
    default: return {};
  }
}

// What goes to the server: numbers for years, nothing for blanks.
function paramsOf(values) {
  const out = {};
  Object.entries(values || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    out[key] = key === 'year' ? Number(value) : value;
  });
  return out;
}

function cellText(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function ReportTable({ report, data }) {
  const columns = data.columns || [];
  const rows = data.rows || [];
  const shown = rows.slice(0, MAX_ROWS_ON_SCREEN);
  const numeric = new Set(columns.filter((c) => rows.some((r) => typeof r[c.key] === 'number')).map((c) => c.key));
  if (rows.length === 0) {
    return <EmptyState compact icon={report.icon} title="Nothing to show">No rows for these inputs. Try another year or range.</EmptyState>;
  }
  return (
    <>
      <div className="hrp-table-wrap hrp-rp__wrap">
        <table className="hrp-table hrp-stack-table hrp-rp__table">
          <caption className="hrp-sr">{report.title}</caption>
          <thead>
            <tr>{columns.map((c) => <th key={c.key} scope="col" data-num={numeric.has(c.key) ? 'true' : undefined}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {shown.map((row, i) => (
              <tr key={i}>
                {columns.map((c) => {
                  const value = row[c.key];
                  return (
                    <td key={c.key} data-label={c.label} data-num={numeric.has(c.key) ? 'true' : undefined}>
                      {cellText(value) || <span className="hrp-dim">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="hrp-rp__count">
        {rows.length > shown.length
          ? `Showing the first ${shown.length} of ${rows.length.toLocaleString('en-US')} rows. Download the CSV for all of them.`
          : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
      </p>
    </>
  );
}

function ReportPanel({ report, people, yearRange, today }) {
  const [draft, setDraft] = useState(() => defaultsFor(report, yearRange, today));
  const [applied, setApplied] = useState(draft);
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const latest = useRef(0);
  const year = yearRange && yearRange.year ? yearRange.year : Number(String(today).slice(0, 4));
  const years = Array.from({ length: 6 }, (_, i) => year + 1 - i);

  const appliedKey = JSON.stringify(applied);
  useEffect(() => {
    latest.current += 1;
    const call = latest.current;
    setState((s) => ({ status: 'loading', data: s.data, error: '' }));
    hr.admin.reports.get(report.name, paramsOf(JSON.parse(appliedKey)))
      .then((data) => { if (call === latest.current) setState({ status: 'ready', data: data || { columns: [], rows: [] }, error: '' }); })
      .catch((err) => { if (call === latest.current) setState({ status: 'error', data: null, error: errorText(err, "Couldn't load this report. Try again.") }); });
    return () => { latest.current += 1; };
  }, [report.name, appliedKey]);

  const set = (key) => (e) => {
    const { value } = e.target;
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const show = (e) => {
    e.preventDefault();
    setApplied({ ...draft });
  };
  const changed = JSON.stringify(draft) !== appliedKey;
  const active = ((people && people.data && people.data.people) || []).slice().sort((a, b) => fullName(a).localeCompare(fullName(b)));

  let inputs = null;
  if (report.inputs === 'year') {
    inputs = (
      <Field label="Benefit year" type="select" value={draft.year} onChange={set('year')} options={years.map((y) => ({ value: String(y), label: String(y) }))} className="hrp-rp__in" />
    );
  } else if (report.inputs === 'range') {
    inputs = (
      <>
        <Field label="From" type="date" value={draft.from} onChange={set('from')} className="hrp-rp__in" />
        <Field label="To" type="date" value={draft.to} onChange={set('to')} className="hrp-rp__in" />
      </>
    );
  } else if (report.inputs === 'ledger') {
    inputs = (
      <>
        <Field label="Benefit year" type="select" value={draft.year} onChange={set('year')} className="hrp-rp__in"
          options={[{ value: '', label: 'Every year' }, ...years.map((y) => ({ value: String(y), label: String(y) }))]} />
        <Field label="Person" type="select" value={draft.employeeId} onChange={set('employeeId')} className="hrp-rp__in hrp-rp__in--wide"
          options={[{ value: '', label: 'Everyone' }, ...active.map((p) => ({ value: p.id, label: fullName(p) }))]} />
      </>
    );
  }

  return (
    <Card delay={1} as="section" aria-label={report.title} className="hrp-rp__panel">
      <div className="hrp-rp__phead">
        <div>
          <h2 className="hrp-card__title">{report.title}</h2>
          <p className="hrp-rp__ptext">{report.text}</p>
        </div>
        <Btn variant="ghost" size="sm" icon={FiDownload} href={hr.admin.reports.csvUrl(report.name, paramsOf(applied))} download disabled={state.status === 'error'}>Download CSV</Btn>
      </div>
      {inputs && (
        <form className="hrp-rp__form" onSubmit={show} noValidate>
          {inputs}
          <Btn type="submit" size="sm" variant={changed ? 'main' : 'ghost'} icon={FiPlay} className="hrp-rp__go">Show report</Btn>
        </form>
      )}
      {state.status === 'error' && <FormError className="hrp-rp__err">{state.error}</FormError>}
      {state.status === 'loading' && !state.data && <Skeleton label="Loading the report" lines={6} height={16} />}
      {state.data && (
        <div className="hrp-rp__res" aria-busy={state.status === 'loading' || undefined}>
          <ReportTable report={report} data={state.data} />
        </div>
      )}
    </Card>
  );
}

export function ReportsPage({ today, people, yearRange }) {
  const [search, setSearch] = useSearchParams();
  const asked = search.get('report');
  const [name, setName] = useState(REPORTS.some((r) => r.name === asked) ? asked : 'balances');
  const report = useMemo(() => REPORTS.find((r) => r.name === name) || REPORTS[0], [name]);

  const pick = (next) => {
    setName(next);
    if (search.get('report')) setSearch({}, { replace: true });
  };

  return (
    <div className="hrp-page hrp-rp">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Reports</h1>
          <p className="hrp-sub">View any report here, or download it as a CSV for Excel. Days are 8 hours.</p>
        </div>
      </div>
      <div className="hrp-rp__pick" role="group" aria-label="Choose a report">
        {REPORTS.map((r, i) => {
          const Icon = r.icon;
          return (
            <button
              key={r.name}
              type="button"
              className="hrp-rp__card"
              aria-pressed={r.name === name}
              style={{ '--hrp-delay': `${40 + i * 40}ms` }}
              onClick={() => pick(r.name)}
            >
              <span className="hrp-rp__ic" aria-hidden="true"><Icon /></span>
              <span className="hrp-rp__ct">
                <b>{r.title}</b>
                <span>{r.text}</span>
              </span>
            </button>
          );
        })}
      </div>
      <ReportPanel key={report.name} report={report} people={people} yearRange={yearRange} today={today} />
    </div>
  );
}

export default ReportsPage;
